import { ContainsDropOversWrapper, Portal } from "client/Components";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { DocumentStoreType } from "client/Api/Documents/common";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	useFolderNavigationStack,
	FolderNavigationStackItem,
	FolderNavigationStack,
	RootFolderId
} from "./useFolderNavigationStack";
import { WebDavPageContent } from "./WebDavPageContent";
import { WebDavSideBarMenu } from "./WebDavSideBarMenu";
import { IStoreHistoryState } from "client_legacy/Documents/documentStore";
import { DocumentLayoutKind } from "client/Api/Documents/documents";
import {
	getFolderNavigationStack,
	FileSystemItemModel,
	WebDavServerModel,
	listServers
} from "client/Api/Documents/WebDav/webDav";
import { useUnableToOpenDocumentLocationDialog } from "../UnableToOpenDocumentLocationDialog";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getWebDavFolderUrl } from "../routing";
import { FileSelectionEntry } from "client_legacy/FileUploader/utils";
import { UploadBatchHandlers } from "../DocumentUploader/useDocumentUploader";

export type ServerLoadingState = { kind: "loading"; }
	| { kind: "error"; }
	| {
		kind: "success";
		servers: readonly WebDavServerModel[];
	};

export type WebDavStoreProps = {
	hasInitializationError: Boolean;
	navigationState: WebDavHistoryState | WebDavActivationContext | undefined;
	onChangeNavigationState(state: WebDavHistoryState): void;
	sideBarContainer: HTMLElement;
	onSideBarNavigation(): void;
	onDoesSidebarContainDropOversChange(value: boolean): void;
	webDavConfiguration: WebDavConfiguration;
	layoutKind: DocumentLayoutKind;
	onLayoutKindChange(kind: DocumentLayoutKind): void;
	onDocumentUpload: (
		files: readonly FileSelectionEntry[],
		handlers: UploadBatchHandlers
	) => void;
};

export const WebDavStore = (props: WebDavStoreProps): ReactElement => {

	const {
		hasInitializationError,
		navigationState,
		onChangeNavigationState,
		sideBarContainer,
		onSideBarNavigation,
		onDoesSidebarContainDropOversChange,
		webDavConfiguration,
		layoutKind,
		onLayoutKindChange,
		onDocumentUpload
	} = props;

	const stack: FolderNavigationStack = useFolderNavigationStack();

	const [serverLoadingState, setServerLoadingState] = useState<ServerLoadingState>({ kind: "loading" });
	const [currentServer, setCurrentServer] = useState<WebDavServerModel | undefined>();
	const stackLoadingCancellation: RefObject<AbortController | null> = useRef<AbortController>(null);
	const setLocationRef: RefObject<SetLocation | null> = useRef<SetLocation>(null);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const [unableToOpenDocumentLocationDialog, openUnableToOpenDocumentLocationDialog] = useUnableToOpenDocumentLocationDialog();

	const currentFolder = useMemo(
		(): FolderNavigationStackItem | undefined => {
			return stack.items[stack.items.length - 1];
		},
		[stack.items]
	);

	const isActive: boolean = isNotNil(navigationState);

	const loadServers = useCallback(
		async (): Promise<void> => {
			try {
				setServerLoadingState({ kind: "loading" });
				const servers: readonly WebDavServerModel[] = await listServers(globalCancellation);

				setServerLoadingState(
					servers.length > 0
						? {
							kind: "success",
							servers
						}
						: { kind: "error" }
				);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}
				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setServerLoadingState({ kind: "error" });
			}
		},
		[
			alertManager,
			globalCancellation
		]
	);

	useEffect(
		() => {
			if (hasInitializationError) {
				setServerLoadingState({ kind: "error" });
			} else {
				loadServers();
			}
		},
		[
			alertManager,
			hasInitializationError,
			loadServers
		]
	);

	const setLocation = useCallback(
		(server: WebDavServerModel, folder: FolderNavigationStackItem | nil): void => {
			stackLoadingCancellation.current?.abort();

			const rootFolder: FolderNavigationStackItem = createRootFolder(server);
			const serverHasChanged: boolean = currentServer?.id !== server.id;
			const currentFolderId: string | undefined = currentFolder?.id;
			const newFolderId: string = folder?.id ?? RootFolderId;
			const folderHasChanged: boolean = newFolderId !== currentFolderId;

			// We try our best to not have to load the navigation stack from the server as it degrades
			// user experience
			let doesTheStackNeedToBeLoadedFromTheServer: boolean = false;

			if (!serverHasChanged && !folderHasChanged) {
				return;
			} else if (serverHasChanged) {
				setCurrentServer(server);

				if (newFolderId === RootFolderId) {
					stack.set([rootFolder]);
				} else {
					doesTheStackNeedToBeLoadedFromTheServer = true;
				}
			} else if (folderHasChanged) {
				const isFolderPresentInExistingStack: boolean = stack.items
					.findIndex(x => x.id === newFolderId) !== -1;

				if (isFolderPresentInExistingStack) {
					stack.navigateBackTo(newFolderId);
				} else if (newFolderId === RootFolderId) {
					stack.set([rootFolder]);
				} else if (folder && folder.parentId === currentFolderId) {
					stack.navigateForwardTo({
						id: folder.id,
						parentId: folder.parentId,
						name: folder.name
					});
				} else {
					doesTheStackNeedToBeLoadedFromTheServer = true;
				}
			}

			if (doesTheStackNeedToBeLoadedFromTheServer) {
				const cancellation = new AbortController();
				stackLoadingCancellation.current = cancellation;

				getFolderNavigationStack(server.id, newFolderId, cancellation.signal)
					.then((folders) => {
						const stackItems: FolderNavigationStackItem[] = folders.map(
							x => {
								const {
									id,
									parentId,
									name
								} = x;

								return {
									id,
									parentId,
									name
								};
							}
						);

						// insert the server (root folder) at the start
						stackItems.unshift(rootFolder);

						stack.set(stackItems);
					})
					.catch((e) => {
						if (isCancelledError(e)) {
							return;
						}
						console.error("Failed to load the folder stack from the backend", e);
						stack.reset();
					});
			}
		},
		[
			currentServer?.id,
			currentFolder?.id,
			stack
		]
	);
	setLocationRef.current = setLocation;

	useEffect(
		(): void => {
			if (!navigationState) {
				setCurrentServer(undefined);
				return;
			}

			if (hasDidRequestSpecificLocationButCouldNotServe(navigationState)
				&& navigationState.didRequestSpecificLocationButCouldNotServe
			) {
				openUnableToOpenDocumentLocationDialog()
					.then(() => {
						onChangeNavigationState({
							store: DocumentStoreType.WebDav,
							server: navigationState.server,
							folder: !navigationState.folder
								? createRootFolder(navigationState.server)
								: {
									id: navigationState.folder.id,
									parentId: navigationState.folder.parentId,
									name: navigationState.folder.name
								}
						});
					});

				return;
			}

			if (!navigationState.folder) {
				onChangeNavigationState({
					store: DocumentStoreType.WebDav,
					server: navigationState.server,
					folder: createRootFolder(navigationState.server)
				});
				return;
			}

			// Transform the folder to be a FolderNavigationStackItem always
			const activationFolder: FolderNavigationStackItem = {
				id: navigationState.folder.id,
				parentId: navigationState.folder.parentId,
				name: navigationState.folder.name
			};

			// We use a ref to the latest "setLocation" callback, because the "setLocation" has a dependency on the navigation stack
			// and it mutates the navigation stack. If we were to do it proper, it would mean that this useEffect fires multiple
			// times due to the "setLocation" function changes, while we intend to only have it "fire" when the navigationState (prop)
			// changes.
			setLocationRef.current?.(navigationState.server, activationFolder);
		},
		[
			navigationState,
			onChangeNavigationState,
			openUnableToOpenDocumentLocationDialog
		]
	);

	const selectServerHandler = useCallback(
		(server: WebDavServerModel): void => {
			const state: WebDavHistoryState = {
				store: DocumentStoreType.WebDav,
				server,
				folder: createRootFolder(server)
			};
			onChangeNavigationState(state);
			onSideBarNavigation();
		},
		[
			onChangeNavigationState,
			onSideBarNavigation
		]
	);

	const openFolderHandler = useCallback(
		(folder: FolderNavigationStackItem): void => {
			const state: WebDavHistoryState = {
				store: DocumentStoreType.WebDav,
				server: ensureNotNil(currentServer),
				folder
			};
			onChangeNavigationState(state);
		},
		[
			currentServer,
			onChangeNavigationState
		]
	);

	const renderPageContent = (): ReactElement | null => {
		if (!isActive || !currentServer) {
			return null;
		}

		return (
			<WebDavPageContent
				isMobile={isMobile}
				isResponsive={isResponsive}
				server={currentServer}
				folderPath={stack.items}
				onOpenFolder={openFolderHandler}
				webDavConfiguration={webDavConfiguration}
				layoutKind={layoutKind}
				onLayoutKindChange={onLayoutKindChange}
				onDocumentUpload={onDocumentUpload}
			/>
		);
	};

	return (
		<>
			<Portal container={sideBarContainer}>
				<ContainsDropOversWrapper onDoesContainDropOversChange={onDoesSidebarContainDropOversChange}>
					<WebDavSideBarMenu
						currentServerId={currentServer?.id}
						serverLoadingState={serverLoadingState}
						onSelectServer={selectServerHandler}
						onReloadSideBar={loadServers}
					/>
				</ContainsDropOversWrapper>
			</Portal>
			{renderPageContent()}
			{unableToOpenDocumentLocationDialog}
		</>
	);
};
WebDavStore.displayName = "WebDavStore";

function createRootFolder(server: WebDavServerModel): FolderNavigationStackItem {
	return {
		id: RootFolderId,
		parentId: null,
		name: server.name,
	};
}

type SetLocation = (server: WebDavServerModel, folder: FolderNavigationStackItem | nil) => void;

export interface WebDavHistoryState extends IStoreHistoryState {
	store: DocumentStoreType.WebDav;
	server: WebDavServerModel;
	folder?: FolderNavigationStackItem | null;
}

export interface WebDavActivationContext {
	server: WebDavServerModel;
	folder: FileSystemItemModel | FolderNavigationStackItem | nil;
	didRequestSpecificLocationButCouldNotServe?: boolean;
}

export function createWebDavUrlFromState(state: IStoreHistoryState): string {
	if (state.store !== DocumentStoreType.WebDav) {
		throw new Error("State is not for WebDav");
	}

	if (!hasServer(state)) {
		throw new Error("State has no server specified");
	}

	const serverId: number = state.server.id;
	const folderId: string | undefined = hasFolder(state)
		? state.folder.id
		: undefined;

	return getWebDavFolderUrl(
		serverId,
		folderId === RootFolderId
			? undefined
			: folderId
	);
}

export function buildWebDavActivationContext(
	server: WebDavServerModel,
	folder?: FileSystemItemModel
): WebDavActivationContext {
	return {
		server,
		folder
	};
}

function hasServer(value: unknown): value is { server: WebDavServerModel; } {
	return isNotNil(
		(value as WebDavHistoryState | WebDavActivationContext ).server
	);
}

function hasFolder(value: unknown): value is { folder: FileSystemItemModel | FolderNavigationStackItem; } {
	return isNotNil(
		(value as WebDavHistoryState | WebDavActivationContext).folder
	);
}

function hasDidRequestSpecificLocationButCouldNotServe(value: unknown): value is { didRequestSpecificLocationButCouldNotServe: NonNullable<WebDavActivationContext["didRequestSpecificLocationButCouldNotServe"]>; } {
	return isNotNil(
		(value as WebDavActivationContext).didRequestSpecificLocationButCouldNotServe
	);
};